import React, { useState, useEffect, useRef } from 'react';

import { updateUser } from '../../../api/usersApi';
import { User } from '../../../api/usersApi';
import { notify } from '../../../shared/feedback/notify';
import { useIsTi } from '../../../hooks/useUserType';

import './UpdateUserModal.css';

interface UpdateUserModalProps {
  isOpen: boolean;
  onCancel: () => void;
  user: User;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
}

const displayToEnumMap: Record<string, string> = {
  "Colaborador": "COLABORADOR",
  "Recepção": "RECEPCAO",
  "Chefe de Oficina": "CHEFE_DE_OFICINA",
  "Orçamento": "ORCAMENTO",
  "Compra": "COMPRA",
  "Estoque": "ESTOQUE",
  "Consultor": "CONSULTOR",
  "Regional": "REGIONAL",
  "Relacionamento": "RELACIONAMENTO",
  "Contac": "CONTAC",
  "Administração": "ADMINISTRACAO",
  "Administrador": "ADMIN",
  
  "COLABORADOR": "COLABORADOR",
  "RECEPCAO": "RECEPCAO",
  "CHEFE_DE_OFICINA": "CHEFE_DE_OFICINA",
  "ORCAMENTO": "ORCAMENTO",
  "COMPRA": "COMPRA",
  "ESTOQUE": "ESTOQUE",
  "CONSULTOR": "CONSULTOR",
  "REGIONAL": "REGIONAL",
  "RELACIONAMENTO": "RELACIONAMENTO",
  "CONTAC": "CONTAC",
  "ADMINISTRACAO": "ADMINISTRACAO",
  "ADMIN": "ADMIN",
  "COMISSIONAMENTO": "COMISSIONAMENTO",
  "TI": "TI",
  "RASTREADOR": "RASTREADOR",
  "EVENTOS": "EVENTOS",
  "INSPETOR": "INSPETOR",
  "TELAO": "TELAO",
  "Rastreador": "RASTREADOR",
  "Eventos": "EVENTOS",
  "Inspetor": "INSPETOR",
  "Telão": "TELAO",
  "GESTAO_COMPRAS": "GESTAO_COMPRAS",
  "Gestão de Compras": "GESTAO_COMPRAS",
  "Comissionamento": "COMISSIONAMENTO"
};

const normalizeUserType = (type: string | undefined | null): string => {
  if (!type) return '';
  return displayToEnumMap[type] || type;
};

const UpdateUserModal: React.FC<UpdateUserModalProps> = ({ isOpen, onCancel, user, setUser, sidebarRef }) => {
  const [updatedUser, setUpdatedUser] = useState({
    fullName: user.fullName || '',
    email: user.email,
    username: user.username,
    password: '',
    confirmPassword: '',
    userType: normalizeUserType(user.userType),
  });

  const modalRef = useRef<HTMLDivElement>(null);
  // TI não pode se atribuir ADMIN (mesma trava do modal de outro usuário, at-25).
  const isTi = useIsTi();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef]);

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!updatedUser.fullName || !updatedUser.email || !updatedUser.username) {
        throw new Error('Nome, e-mail e nome de usuário não podem ser vazios.');
      }

      if (updatedUser.password && updatedUser.password !== updatedUser.confirmPassword) {
        throw new Error('As senhas não coincidem.');
      }

      const updateUserDto = {
        fullName: updatedUser.fullName,
        email: updatedUser.email,
        username: updatedUser.username,
        userType: updatedUser.userType,
        ...(updatedUser.password ? { password: updatedUser.password } : {}),
        ...(updatedUser.password ? { confirmPassword: updatedUser.confirmPassword } : {}),
      };

      await updateUser(user.id, updateUserDto);
      notify.success('Informações atualizadas com sucesso!');
      onCancel();
    } catch (error: any) {
      // Antes o erro era gravado num estado que a tela nunca exibia — salvar falhava em
      // silêncio (mesmo padrão do bug do at-26 em "Minhas informações", corrigido na at-38).
      // O errorFrom também dá voz às validações locais (senhas não coincidem, campos vazios).
      notify.errorFrom(error, 'Erro ao atualizar as informações');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="edit-car-modal-overlay update-user-modal-specific">
      <div className="edit-car-modal-content" ref={modalRef}>
        <div onSubmit={handleUpdateUser} className="lightbox-update-user">
          <form className="LightboxLogin">
            <div className="EditarCarro">Editar informações</div>
            <div className="Frame1000004010">
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Nome Completo</div>
                  <div className="CampoInput">
                    <input className="Input" type="text" name="name" value={updatedUser.fullName} onChange={(e) => setUpdatedUser({ ...updatedUser, fullName: e.target.value })} />
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Email</div>
                  <div className="CampoInput">
                    <input className="Input" type="text" name="email" value={updatedUser.email} onChange={(e) => setUpdatedUser({ ...updatedUser, email: e.target.value })} />
                  </div>
                </div>
              </div>
            </div>
            <div className="Frame1000004010">
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Nome de Usuário</div>
                  <div className="DropDownFormulRio">
                    <div className="InputSelect">
                      <input className="Input" type="text" name="username" value={updatedUser.username} onChange={(e) => setUpdatedUser({ ...updatedUser, username: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Tipo de Usuário</div>
                  <div className="DropDownFormulRio">
                    <div className="InputSelect">
                      <select
                        className="Input"
                        value={updatedUser.userType}
                        onChange={(e) => setUpdatedUser({ ...updatedUser, userType: e.target.value })}
                        style={{ border: 'none', width: '100%', outline: 'none', background: 'transparent' }}
                      >
                        <option value="">Selecione...</option>
                        {/* Ordem alfabética pelo RÓTULO (pedido do Victor, 25/09/2026). */}
                        <option value="ADMINISTRACAO">Administração</option>
                        <option value="CHEFE_DE_OFICINA">Chefe de Oficina</option>
                        <option value="COLABORADOR">Colaborador</option>
                        <option value="COMISSIONAMENTO">Comissionamento</option>
                        <option value="COMPRA">Compra</option>
                        <option value="CONSULTOR">Consultor</option>
                        <option value="CONTAC">Contac</option>
                        <option value="ESTOQUE">Estoque</option>
                        <option value="EVENTOS">Eventos</option>
                        <option value="GESTAO_COMPRAS">Gestão de Compras</option>
                        <option value="INSPETOR">Inspetor</option>
                        <option value="TELAO">Telão</option>
                        <option value="ORCAMENTO">Orçamento</option>
                        <option value="RASTREADOR">Rastreador</option>
                        <option value="RECEPCAO">Recepção</option>
                        <option value="REGIONAL">Regional</option>
                        <option value="RELACIONAMENTO">Relacionamento</option>
                        <option value="TI">TI</option>
                        {(!isTi || normalizeUserType(user.userType) === 'ADMIN') && (
                          <option value="ADMIN">Administrador</option>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="Frame1000004010">
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Senha</div>
                  <div className="CampoInput">
                    <input className="Input" type="password" name="password" autoComplete="new-password" value={updatedUser.password} onChange={(e) => setUpdatedUser({ ...updatedUser, password: e.target.value })} />
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Confirmar Senha</div>
                  <div className="CampoInput">
                    <input className="Input" type="password" name="confirmPassword" autoComplete="new-password" value={updatedUser.confirmPassword} onChange={(e) => setUpdatedUser({ ...updatedUser, confirmPassword: e.target.value })} />
                  </div>
                </div>
              </div>
            </div>
            <div className="Frame1000003998">
              <div className="CtaBigEditar cancel" onClick={onCancel}>
                <button className="CustomFileCancel" type="button">Cancelar</button>
              </div>
              <button className="CtaBigEditar save" type="submit">Salvar</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateUserModal;
